import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface Approval {
  id: string; school_id: string; school_name: string; school_code: string;
  teacher_name_ms: string; teacher_name_en: string | null;
  ic_number: string; nationality: string | null;
  highest_qualification: string | null; teaching_subjects: string[];
  years_experience: number | null; status: string;
  conditions_ms: string | null; approval_expiry: string | null;
  approved_by: string | null; approved_at: string | null;
  created_at: string;
}

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  PENDING:     { label: 'Menunggu',    color: '#facc15' },
  APPROVED:    { label: 'Diluluskan', color: '#4ade80' },
  CONDITIONAL: { label: 'Bersyarat',  color: '#60a5fa' },
  REJECTED:    { label: 'Ditolak',    color: '#f87171' },
  EXPIRED:     { label: 'Tamat',      color: '#9ca3af' },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function DPEApprovalsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === "en";
  const [approvals,  setApprovals]  = useState<Approval[]>([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [toast,      setToast]      = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterSchoolId, setFilterSchoolId] = useState('');

  // Submit new approval form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    schoolId: '', teacherNameMs: '', teacherNameEn: '',
    icNumber: '', nationality: '', highestQualification: '',
    teachingSubjects: '', yearsExperience: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  // Update approval modal
  const [updating,    setUpdating]    = useState<string | null>(null);
  const [updateForm,  setUpdateForm]  = useState({ status: '', conditionsMs: '', approvalExpiry: '' });
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updatingId,  setUpdatingId]  = useState<string | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (filterStatus)   params.set('status',   filterStatus);
      if (filterSchoolId.trim() && UUID_RE.test(filterSchoolId.trim())) params.set('schoolId', filterSchoolId.trim());
      const result = await apiGet<{ data: Approval[]; total: number }>(`/api/dpe/teacher-approvals?${params}`);
      setApprovals(result.data); setTotal(result.total); setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan kelulusan. / Failed to load approvals.');
    } finally { setLoading(false); }
  }, [filterStatus, filterSchoolId]);

  useEffect(() => { void load(1); }, [load]);

  function validateForm(): string | null {
    if (!form.schoolId.trim())      return 'ID sekolah diperlukan. / School ID required.';
    if (!UUID_RE.test(form.schoolId.trim())) return 'ID sekolah tidak sah (mesti UUID). / Invalid school ID (must be UUID).';
    if (!form.teacherNameMs.trim()) return 'Nama guru (BM) diperlukan. / Teacher name (BM) required.';
    if (!form.icNumber.trim())      return 'Nombor IC diperlukan. / IC number required.';
    if (form.icNumber.trim().length > 20) return 'Nombor IC terlalu panjang (maks 20 aksara). / IC number too long (max 20 chars).';
    if (form.yearsExperience && (isNaN(parseInt(form.yearsExperience)) || parseInt(form.yearsExperience) < 0 || parseInt(form.yearsExperience) > 60))
      return 'Pengalaman mesti 0–60 tahun. / Experience must be 0–60 years.';
    return null;
  }

  async function handleSubmit() {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);
    try {
      await apiPost('/api/dpe/teacher-approvals', {
        schoolId:            form.schoolId.trim(),
        teacherNameMs:       form.teacherNameMs.trim(),
        teacherNameEn:       form.teacherNameEn.trim() || undefined,
        icNumber:            form.icNumber.trim(),
        nationality:         form.nationality.trim() || undefined,
        highestQualification:form.highestQualification.trim() || undefined,
        teachingSubjects:    form.teachingSubjects.split(',').map((s) => s.trim()).filter(Boolean),
        yearsExperience:     form.yearsExperience ? parseInt(form.yearsExperience) : undefined,
      });
      setToast({ variant: 'success', message: 'Permohonan berjaya dihantar. / Application submitted.' });
      setShowForm(false);
      setForm({ schoolId: '', teacherNameMs: '', teacherNameEn: '', icNumber: '', nationality: '', highestQualification: '', teachingSubjects: '', yearsExperience: '' });
      void load(1);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal menghantar permohonan. / Failed to submit.');
    } finally { setSaving(false); }
  }

  function openUpdate(a: Approval) {
    setUpdating(a.id);
    setUpdateForm({ status: a.status, conditionsMs: a.conditions_ms ?? '', approvalExpiry: a.approval_expiry ?? '' });
    setUpdateError(null);
  }

  function validateUpdate(): string | null {
    if (!updateForm.status) return 'Status diperlukan. / Status required.';
    if ((updateForm.status === 'APPROVED' || updateForm.status === 'CONDITIONAL') && updateForm.conditionsMs && updateForm.conditionsMs.length > 2000)
      return 'Syarat terlalu panjang (maks 2000). / Conditions too long (max 2000).';
    if (updateForm.approvalExpiry) {
      const d = new Date(updateForm.approvalExpiry);
      if (isNaN(d.getTime())) return 'Tarikh luput tidak sah. / Invalid expiry date.';
      if (d < new Date()) return 'Tarikh luput mesti pada masa hadapan. / Expiry date must be in the future.';
    }
    return null;
  }

  async function handleUpdate() {
    const err = validateUpdate();
    if (err) { setUpdateError(err); return; }
    if (!updating) return;
    setUpdatingId(updating); setUpdateError(null);
    try {
      await apiPatch(`/api/dpe/teacher-approvals/${updating}`, {
        status:         updateForm.status,
        conditionsMs:   updateForm.conditionsMs.trim() || undefined,
        approvalExpiry: updateForm.approvalExpiry || undefined,
      });
      setToast({ variant: 'success', message: 'Status kelulusan dikemaskini. / Approval status updated.' });
      setUpdating(null);
      void load(page);
    } catch (e) {
      setUpdateError(e instanceof Error ? e.message : 'Gagal kemaskini. / Failed to update.');
    } finally { setUpdatingId(null); }
  }

  const totalPages = Math.ceil(total / 25) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/dpe" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← DPE</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                👨‍🏫 Kelulusan Guru Swasta
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Private Teacher Approvals — {total} permohonan
              </p>
            </div>
            {!showForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setFormError(null); }}>
                + Hantar Permohonan
              </Button>
            )}
          </div>

          {/* Submit form */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                Permohonan Kelulusan Guru Baharu / New Teacher Approval Application
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="ID Sekolah *" style={{ flex: '2 1 200px' }}>
                    <input type="text" value={form.schoolId} onChange={(e) => setForm((f) => ({ ...f, schoolId: e.target.value }))}
                      placeholder="UUID sekolah" disabled={saving} style={inputSt()} />
                  </LF>
                  <LF label="No. IC *" style={{ flex: '1 1 140px' }}>
                    <input type="text" value={form.icNumber} onChange={(e) => setForm((f) => ({ ...f, icNumber: e.target.value }))}
                      placeholder="cth: 900101-12-3456" disabled={saving} style={inputSt()} maxLength={20} />
                  </LF>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Nama Guru (BM) *" style={{ flex: '2 1 200px' }}>
                    <input type="text" value={form.teacherNameMs} onChange={(e) => setForm((f) => ({ ...f, teacherNameMs: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={200} />
                  </LF>
                  <LF label="Teacher Name (EN)" style={{ flex: '2 1 200px' }}>
                    <input type="text" value={form.teacherNameEn} onChange={(e) => setForm((f) => ({ ...f, teacherNameEn: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={200} />
                  </LF>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Kewarganegaraan" style={{ flex: '1 1 140px' }}>
                    <input type="text" value={form.nationality} onChange={(e) => setForm((f) => ({ ...f, nationality: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={100} />
                  </LF>
                  <LF label="Kelayakan Tertinggi" style={{ flex: '2 1 200px' }}>
                    <input type="text" value={form.highestQualification} onChange={(e) => setForm((f) => ({ ...f, highestQualification: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={200} />
                  </LF>
                  <LF label="Pengalaman (tahun)" style={{ flex: '1 1 120px' }}>
                    <input type="number" value={form.yearsExperience} onChange={(e) => setForm((f) => ({ ...f, yearsExperience: e.target.value }))}
                      disabled={saving} min={0} max={60} style={inputSt()} />
                  </LF>
                </div>
                <LF label="Mata Pelajaran (pisahkan dengan koma / comma-separated)">
                  <input type="text" value={form.teachingSubjects} onChange={(e) => setForm((f) => ({ ...f, teachingSubjects: e.target.value }))}
                    placeholder="cth: Matematik, Sains, Bahasa Melayu" disabled={saving} style={inputSt()} />
                </LF>
              </div>
              {formError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleSubmit()} disabled={saving} loading={saving}>
                  {saving ? 'Menghantar…' : 'Hantar / Submit'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setFormError(null); }} disabled={saving}>Batal</Button>
              </div>
            </div>
          )}

          {/* Update modal */}
          {updating && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: spacing.md }}>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, width: '100%', maxWidth: '480px', boxShadow: shadows.xl }}>
                <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                  Kemaskini Status Kelulusan / Update Approval Status
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  <LF label="Status *">
                    <select value={updateForm.status} onChange={(e) => setUpdateForm((f) => ({ ...f, status: e.target.value }))}
                      disabled={!!updatingId} style={inputSt()}>
                      {Object.entries(STATUS_CFG).map(([v, c]) => <option key={v} value={v}>{c.label} ({v})</option>)}
                    </select>
                  </LF>
                  <LF label="Syarat / Conditions (BM)">
                    <textarea value={updateForm.conditionsMs} onChange={(e) => setUpdateForm((f) => ({ ...f, conditionsMs: e.target.value }))}
                      rows={3} disabled={!!updatingId} maxLength={2000} style={{ ...inputSt(), resize: 'vertical' }} />
                  </LF>
                  <LF label="Tarikh Luput Kelulusan">
                    <input type="date" value={updateForm.approvalExpiry} min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setUpdateForm((f) => ({ ...f, approvalExpiry: e.target.value }))}
                      disabled={!!updatingId} style={inputSt()} />
                  </LF>
                </div>
                {updateError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{updateError}</p>}
                <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                  <Button variant="primary" size="sm" onClick={() => void handleUpdate()} disabled={!!updatingId} loading={!!updatingId}>
                    {updatingId ? 'Menyimpan…' : 'Simpan / Save'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setUpdating(null); setUpdateError(null); }} disabled={!!updatingId}>Batal</Button>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelSt}>Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading} style={inputSt()}>
                  <option value="">Semua</option>
                  {Object.entries(STATUS_CFG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
                </select>
              </div>
              <div style={{ flex: '2 1 220px' }}>
                <label style={labelSt}>ID Sekolah (UUID)</label>
                <input type="text" value={filterSchoolId} onChange={(e) => setFilterSchoolId(e.target.value)}
                  placeholder="Tapis mengikut sekolah…" disabled={loading} style={inputSt()} />
              </div>
              <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Tapis / Filter'}
              </Button>
            </div>
          </div>

          {error && <ErrorBanner msg={error} onRetry={() => void load(page)} />}
          {loading && approvals.length === 0 && <LoadingBox />}
          {!loading && !error && approvals.length === 0 && (
            <EmptyBox icon="👨‍🏫" title="Tiada Permohonan" body="No teacher approval applications found." />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, opacity: loading ? 0.7 : 1 }}>
            {approvals.map((a) => {
              const sc  = STATUS_CFG[a.status] ?? { label: a.status, color: '#9ca3af' };
              const subjects = Array.isArray(a.teaching_subjects) ? a.teaching_subjects : [];
              const expired  = a.approval_expiry && new Date(a.approval_expiry) < new Date();

              return (
                <div key={a.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${a.status === 'PENDING' ? 'rgba(250,204,21,0.3)' : a.status === 'REJECTED' ? 'rgba(248,113,113,0.2)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Pill label={sc.label} color={sc.color} />
                        {expired && <Pill label="Luput" color="#9ca3af" />}
                      </div>
                      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                        {a.teacher_name_ms}
                        {a.teacher_name_en ? <span style={{ color: colors.neutral.gray400, fontWeight: typography.fontWeight.normal }}> / {a.teacher_name_en}</span> : null}
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        IC: {a.ic_number}
                        {a.nationality ? ` · ${a.nationality}` : ''}
                        {a.highest_qualification ? ` · ${a.highest_qualification}` : ''}
                        {a.years_experience !== null ? ` · ${a.years_experience} tahun` : ''}
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                        {a.school_name} ({a.school_code})
                      </p>
                      {subjects.length > 0 && (
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                          Mata pelajaran: {subjects.join(', ')}
                        </p>
                      )}
                      {a.conditions_ms && (
                        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: '#60a5fa' }}>
                          Syarat: {a.conditions_ms}
                        </p>
                      )}
                      {a.approval_expiry && (
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: expired ? '#f87171' : colors.neutral.gray400 }}>
                          {E ? 'Approval expires:' : 'Luput kelulusan:'} {new Date(a.approval_expiry).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                        </p>
                      )}
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                        {E ? 'Submitted:' : 'Dihantar:'} {new Date(a.created_at).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                      </p>
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => openUpdate(a)}>
                        Kemaskini Status
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl }}>
              <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1)}>← Sebelum</Button>
              <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => void load(page + 1)}>Seterusnya →</Button>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>{label}</span>;
}
function ErrorBanner({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return { width: '100%', backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md, color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box', fontFamily: typography.fontFamily.body };
}
